import React from 'react'

import { BaseText, BaseView } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { useTheme } from '@/hooks/useTheme'
import { WalletCurrency } from '@/domain'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'
import { currencyUtils } from '@/utils'

const { currencyConditionals } = currencyUtils

interface CurrencyItemProps extends WalletCurrency {
  onPress?: (currency: WalletCurrency) => void
  containerStyle?: React.CSSProperties
  contentStyle?: React.CSSProperties
  descriptionIsCryptoValue?: boolean
  showChevronRight?: boolean
  disabled?: boolean
}

export const CurrencyItem: React.FC<CurrencyItemProps> = ({
  onPress,
  containerStyle,
  contentStyle,
  descriptionIsCryptoValue = false,
  showChevronRight = false,
  disabled = false,
  ...item
}) => {
  const theme = useTheme()

  const { isUsdt } = currencyConditionals(item.code)

  const formattedFiatAmount = formatFiatCurrency({
    value: item.fiatAmount,
    showDecimals: true,
    fiatCode: item.fiatCurrency,
  })

  const isLongTextAmountAndDescriptionIsCryptoValue =
    descriptionIsCryptoValue &&
    `${item.amountWithoutNumberConversion} ${item.displayCurrencyCode}${item.revenue24hFiatAmount}`
      .length > 24

  return (
    <CardItem.Root
      disabled={disabled || !onPress}
      onPress={() => onPress?.(item)}
      style={{ borderRadius: 30, ...containerStyle }}
    >
      <CardItem.CurrencyIcon
        path={item.icon}
        bgColor={
          disabled ? theme.colors.background.icon : item.avatarBGRefColor
        }
        size={52}
      />

      <CardItem.Content style={{ gap: 2, ...contentStyle }}>
        <BaseText
          style={disabled ? { color: theme.colors.label.disabled } : undefined}
          fontSize={18}
          fontFamily="bold"
          color="label.default"
        >
          {item.formattedFiatBalance}
        </BaseText>

        <BaseView
          row={!isLongTextAmountAndDescriptionIsCryptoValue}
          align="center"
          justify="space-between"
          fullWidth={!isLongTextAmountAndDescriptionIsCryptoValue}
          gap={isLongTextAmountAndDescriptionIsCryptoValue ? 6 : 0}
        >
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.description"
            style={
              disabled ? { color: theme.colors.label.disabled } : undefined
            }
          >
            {descriptionIsCryptoValue
              ? `${item.amountWithoutNumberConversion} ${item.displayCurrencyCode}`
              : item.name}
          </BaseText>

          {!showChevronRight && (
            <>
              {item.percentageChange24h !== 0 && !isUsdt && (
                <CardItem.FlagProfitsOrLosses
                  percentChange24h={item.percentageChange24h}
                  revenue24hFiatAmount={item.revenue24hFiatAmount}
                  fiatCode={item.fiatCurrency}
                />
              )}
            </>
          )}
        </BaseView>
      </CardItem.Content>

      {showChevronRight && (
        <CardItem.RightIcon>
          <div />
        </CardItem.RightIcon>
      )}
    </CardItem.Root>
  )
}
